import { initializeAnalytics, reset, type Environment } from "@fullscript/fs-analytics";

import { isProduction, isStaging } from "@shared/utils/fullscriptEnv/fullscriptEnv";

type FsAnalyticsWriteKeyContext =
  | "unauth"
  | "practitioner"
  | "patient"
  | "emerson.auth"
  | "emerson.unauth";

/**
 * Rudderstack write keys are publicly visible and have been moved here from their previous hardcoded locations in the define.mjs files.
 * Once Remix environment variables are implemented across the app, we can remove this and use a window.ENV var instead.
 */
const WRITE_KEYS = {
  hwAdmin: {
    unauth: {
      prod: "3FEN106FyqfgZxpg4J8shkju7UC",
      dev: "3FC67jtCwUTpCBLpfIq35tMnOPI",
    },
    practitioner: {
      prod: "3FENBKS94tAOrQjCUySzgszFwvo",
      dev: "3FC6nzqAJ9i8cGGJ5F9pOr31tsH",
    },
    patient: {
      prod: "3FENCqGuVERiVBkotYojYpSSk31",
      dev: "3FC6p3aTJ9J20SYYLilDyLJm9Kr",
    },
  },
  emerson: {
    unauth: {
      prod: "3FEN77hKlBcWb7Wrg6On79gYaWW",
      dev: "3FC6P0Ryw8mDtmjhcHneCeFAeht",
    },
    auth: {
      prod: "3FENFWkZ9tAM1fI8whDtQD814oE",
      dev: "3FC7CcfQdkax035KK1PBBWIeU8A",
    },
  },
} as const;

const DEFAULT_WRITE_KEY_CONTEXT: FsAnalyticsWriteKeyContext = "unauth";

type InitFsAnalyticsOptions = {
  loadOneTrust?: boolean;
};

const FS_ANALYTICS_AUTHENTICATED_SESSION_KEY = "fs_analytics_authenticated_session";

let initialized = false;

const isUnauthContext = (context: FsAnalyticsWriteKeyContext): boolean =>
  context === "unauth" || context === "emerson.unauth";

const getWriteKey = (context: FsAnalyticsWriteKeyContext = DEFAULT_WRITE_KEY_CONTEXT): string => {
  const keys =
    context === "emerson.auth" || context === "emerson.unauth"
      ? WRITE_KEYS.emerson[context === "emerson.auth" ? "auth" : "unauth"]
      : WRITE_KEYS.hwAdmin[context];

  return isProduction() ? keys.prod : keys.dev;
};

const getEnvironment = (): Environment => {
  if (isProduction()) return "production";
  if (isStaging()) return "staging";

  return "dev";
};

const markAuthenticatedSession = (): void => {
  try {
    sessionStorage.setItem(FS_ANALYTICS_AUTHENTICATED_SESSION_KEY, "1");
  } catch {
    // sessionStorage may be unavailable
  }
};

const resetAfterLogout = (): void => {
  try {
    if (!sessionStorage.getItem(FS_ANALYTICS_AUTHENTICATED_SESSION_KEY)) {
      return;
    }
    sessionStorage.removeItem(FS_ANALYTICS_AUTHENTICATED_SESSION_KEY);
    reset();
  } catch {
    // Do not block shell load if reset() or sessionStorage throws
  }
};

/** Idempotent — only the first call initializes analytics. No session side effects. */
const initFsAnalyticsCore = (
  writeKeyContext: FsAnalyticsWriteKeyContext = DEFAULT_WRITE_KEY_CONTEXT,
  { loadOneTrust = isUnauthContext(writeKeyContext) }: InitFsAnalyticsOptions = {}
): boolean => {
  if (initialized) return true;

  initializeAnalytics(getWriteKey(writeKeyContext), getEnvironment(), { loadOneTrust });
  initialized = true;

  return true;
};

const initFsAnalytics = (
  writeKeyContext: FsAnalyticsWriteKeyContext = DEFAULT_WRITE_KEY_CONTEXT
): boolean => {
  initFsAnalyticsCore(writeKeyContext);

  if (isUnauthContext(writeKeyContext)) {
    resetAfterLogout();
  } else {
    markAuthenticatedSession();
  }

  return true;
};

const resetFsAnalyticsRemixIfGuest = (initialProps: { userId?: string } | undefined): void => {
  if (initialProps?.userId) {
    markAuthenticatedSession();

    return;
  }
  resetAfterLogout();
};

export { initFsAnalytics, initFsAnalyticsCore, resetFsAnalyticsRemixIfGuest };

export type { FsAnalyticsWriteKeyContext, InitFsAnalyticsOptions };
